<template>
  <section ref="planDesignTable">
    <plan-and-rate-header v-model="isActive">
      <template v-if="isPlanBased">
        {{ containerInfo.description }}
      </template>
      <template v-else>
        Plan design
      </template>
      <template
        v-if="isPlanBased"
        slot="subtitle"
      >
        Plan design
      </template>
    </plan-and-rate-header>
    <table
      v-if="isActive"
      v-test:table="'plan design attributes'"
    >
      <thead>
        <!-- This is to get the widths set for all of the cells -->
        <tr class="row-cell-width">
          <td />
          <td
            v-if="!isPlanBased"
            class="cell-classes"
          />
          <td
            v-for="cell in subtypeColspans[containerId]"
            :key="`policy-${cell}`"
            :width="`${cellWidth}px`"
          />
        </tr>
      </thead>
      <tbody>
        <template
          v-for="({
            id: categoryId,
            name: categoryName,
            plan_design_attribute_groups: planDesignAttributeGroups,
            plan_design_attributes: categoryPlanDesignAttributes,
          }) in container.categories"
        >
          <tr
            v-if="!ignoreCategories[categoryId].includes(categoryId)"
            :key="categoryName"
          >
            <th
              class="cell-category-name"
              :colspan="isPlanBased ? 1 + subtypeColspans[containerId] : 1"
              v-text="categoryName"
            />
            <template v-if="!isPlanBased">
              <th
                class="cell-label"
                v-text="'Class(es)'"
              />
              <th :colspan="subtypeColspans[containerId]">
                &nbsp;
              </th>
            </template>
          </tr>
          <!-- Each Plan Design Attribute -->
          <template v-if="isPlanBased">
            <template
              v-for="({
                subtypes_by_container_and_product: subtypes,
                plan_design_attributes: planDesignAttributes,
              }, groupIdx) in planDesignAttributeGroups"
            >
              <category-title
                :key="`${categoryName}-${groupIdx}-title`"
                v-bind="{ containerId, subtypes }"
              />
              <template
                v-for="({
                  name: attributeName,
                  products
                }, idx) in planDesignAttributes"
              >
                <plan-design-attribute
                  v-if="products[0].tier_group"
                  :key="`${categoryName}-${attributeName}-${idx}`"
                  v-bind="{
                    attributeName,
                    containerId,
                    idx,
                    subtypes: products[0].tier_group.tier_subtypes,
                  }"
                />
              </template>
            </template>
          </template>
          <template v-else>
            <template
              v-for="({
                groupings,
                name: attributeName,
              }) in categoryPlanDesignAttributes"
            >
              <!--
                This has never existed on the carrier site but I'm guessing we'll want it here.
                The logic is built out but I'll need to run through it with UX first.
                https://watchtower.atlassian.net/browse/CPV-732
              -->
              <!-- <category-title
                :key="`category-title-${categoryName}-${attributeName}`"
                v-bind="{
                  containerId,
                  groupings,
                }"
              /> -->
              <template
                v-for="({
                  label: classLabel,
                  products,
                }, idx) in groupings"
              >
                <plan-design-attribute
                  v-if="products[0].tier_group"
                  :key="`${categoryName}-${attributeName}-${idx}`"
                  v-bind="{
                    attributeName,
                    classLabel,
                    containerId,
                    idx,
                    rowSpan: groupings.length,
                    subtypes: products[0].tier_group.tier_subtypes,
                  }"
                />
              </template>
            </template>
          </template>
        </template>
      </tbody>
    </table>
  </section>
</template>

<script>
  // vuex
  import { mapGetters, mapState } from 'vuex';
  // components
  import PlanAndRateHeader from '@/components/PlanAndRateHeader';
  import CategoryTitle from './CategoryTitle';
  import PlanDesignAttribute from './Attribute';

  /**
   * A component used to display a plan design table based on container and category information from the "planDesign" store module.
   *
   * @vuedoc
   * @exports PlanDesign/Table
   * @category Components
   */
  export default {
    name: 'PlanDesignTable',
    components: {
      CategoryTitle,
      PlanAndRateHeader,
      PlanDesignAttribute,
    },
    props: {
      container: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      isActive: true,
      planDesignTableWidth: 0,
    }),
    computed: {
      ...mapGetters('planDesignAttributes', ['isPlanBased']),
      ...mapState('planDesignAttributes', ['ignoreCategories', 'subtypeColspans']),
      /**
       * Calculates the width of each non-title cell.
       * Attribute names are 250px wide and the class label cell for class based products is 100px.
       *
       * @returns {number}
       */
      cellWidth() {
        const attributeNameWidth = this.isPlanBased ? 250 : 350;

        return (this.planDesignTableWidth - attributeNameWidth) / this.subtypeColspans[this.containerId];
      },
      /**
       * Cache the container ID to be used throughout the template and vue instance.
       *
       * @returns {number}
       */
      containerId() {
        return this.container.id;
      },
      /**
       * Get the appriate container info from the store to render the table title in the template.
       *
       * @returns {number}
       */
      containerInfo() {
        return this.$store.state.planDesignAttributes.containerInfo.find(({ id }) => id === this.containerId);
      },
    },
    mounted() {
      this.planDesignTableWidth = this.$refs.planDesignTable.clientWidth;
    },
  };
</script>

<style lang="scss" scoped>
  section {
    margin-bottom: 20px;
  }

  table {
    width: 100%;
    margin-bottom: 20px;
    table-layout: fixed;
    border: 1px solid $tf-light-gray;
    border-top: 0;
  }

  th {
    padding: 10px 6px;
    background: $tf-extra-light-gray-2;
    text-align: left;
  }

  .cell {
    &-category-name {
      padding-left: 20px;
      border-bottom: 1px solid $tf-light-gray;
    }

    &-classes {
      width: 100px;
    }

    &-label {
      padding: 0;
      text-align: center;
    }
  }

  .row-cell-width {
    td {
      height: 0;
      padding: 0;

      &:first-of-type {
        width: 250px;
      }
    }
  }
</style>
